'use client';

import { fromRow } from '@slims/shared/data';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import { api } from '@/lib/utils/api';
import { useAdminScopeStore } from '@/stores/admin-scope-store';

export type MeterKind = 'M1' | 'M2' | 'M3';
export type MeterTipo = 'withdrawal' | 'injection' | 'storage';
export type TelemetryMode = 'simulation' | 'realtime';

export interface Meter {
	id: string;
	tenantId: string;
	memberId: string;
	/**
	 * Il punto di connessione a cui il punto di misura appartiene. `null` finché
	 * il codice POD non è stato inserito — l'aggiunta rapida del socio crea il
	 * misuratore col solo nome.
	 */
	podId?: string | null;
	codice: string;
	/** Classificazione CACER: M1 prelievo · M2 produzione · M3 accumulo (default DB: M1) */
	kind?: MeterKind;
	tipo: MeterTipo;
	cabinaPrimaria: string | null;
	verificatoGSE: boolean | null;
	dso: string | null;
	// impianto fields (M2 / injection only — may be absent on older DB rows)
	/** Nome parlante dell'impianto («Campo FV Nord»): finché è `null` le card di
	 * monitoraggio (`GET /api/plants`) ripiegano sul CENSIMP e poi sull'id. */
	nome?: string | null;
	areaConvenzionale?: string | null;
	potenzaKwp?: number | null;
	dataEntrataEsercizio?: string | null;
	preEsistente?: boolean | null;
	codiceUP?: string | null;
	codiceGaudi?: string | null;
	dnshConforme?: boolean | null;
	/** Intensità del contributo PNRR in conto capitale per l'impianto (%, es. 30 = 30%). */
	pnrrGrantPct?: number | null;
	tecnologia?: 'fv' | 'eolico' | 'idro' | 'altro' | null;
	/** Moduli dell'impianto: quanti e quanti W ciascuno (Guida SPC GSE). */
	numeroModuli?: number | null;
	potenzaModuloW?: number | null;
	/** Somma degli inverter dell'impianto; scrivibile solo con un inverter o nessuno. */
	potenzaInverterKw?: number | null;
	inverterCount?: number;
	/** Tipologia di cliente finale del POD: domestico, altri usi, ricarica veicoli. */
	tipologiaUtenza?: 'domestico' | 'altri_usi' | 'ricarica_veicoli' | null;
	// accumulo fields (M3 / storage only)
	capacitaKwh?: number | null;
	potenzaKw?: number | null;
	efficienzaCarica?: number | null;
	efficienzaScarica?: number | null;
	// telemetria
	telemetryMode?: TelemetryMode;
	// gateway hardware (push ingestion) — token hash never exposed to the client
	gatewayDeviceId?: string | null;
	gatewayEnabled?: boolean | null;
	gatewayLastSeenAt?: string | null;
	gatewayLastError?: string | null;
	// dati predittivi (misuratore senza connessione live)
	predictiveEnabled?: boolean;
	/** Feed live simulato attivo (campioni sintetici quando manca il dato reale) */
	liveFeedSimulated?: boolean;
	predictiveCalibration?: {
		scale: number;
		hourlyFactors?: number[];
		calibratedAt?: string;
		sampleDays?: number;
	} | null;
	/**
	 * Il codice di profilo GSE scelto a mano. `null` — il caso normale — significa
	 * «segui la deduzione». Quando è valorizzato vince su ruolo, tipo e accumulo,
	 * e nessun automatismo lo sovrascrive.
	 */
	gseProfileCode?: string | null;
	/**
	 * Il profilo tipico GSE **in uso** sul punto di misura, già risolto: il codice
	 * scelto a mano se c'è, quello dedotto da ruolo, tipo, accumulo e impianti
	 * altrimenti. `null` sull'M3, che non ha un profilo (`apps/api/src/typical/gse.ts`).
	 */
	profiloTipico?: {
		/** Codice GSE, es. `PDMM`. */
		codice: string;
		/** Da dove vengono i totali mensili distribuiti dal profilo. */
		basis: 'dichiarato' | 'default_tipo' | 'impianti' | 'default_kwp';
		/** Somma dei dodici mensili, in kWh. */
		annualKwh: number;
	} | null;
	createdAt: string | null;
}

const EMPTY: Meter[] = [];

/** React Query key for the full meter list of one org (filtered per member client-side). */
const metersKey = (orgId: string | null | undefined) => ['meters', orgId] as const;

/**
 * ADR-006: backed by React Query. Il fetch è sull'intera lista misuratori della
 * configurazione (una sola richiesta condivisa da tutte le schede socio); il
 * filtro per `memberId` è un `select`. Le mutation e il realtime aggiornano la
 * lista intera in cache.
 */
export function useMeters(memberId?: string) {
	const queryClient = useQueryClient();
	// Meters live in the admin area: scope every call to the admin configuration.
	const orgId = useAdminScopeStore((s) => s.configId);
	const active = !!memberId && !!orgId;

	const { data, isLoading, error, refetch: rqRefetch } = useQuery({
		queryKey: metersKey(orgId),
		enabled: active,
		queryFn: () => api.get<Meter[]>('/api/meters', { orgId: orgId as string }),
		select: (all) => all.filter((p) => p.memberId === memberId),
	});

	const refetch = useCallback(async () => {
		await rqRefetch();
	}, [rqRefetch]);

	const patchCache = useCallback(
		(update: (prev: Meter[]) => Meter[]) => {
			queryClient.setQueryData<Meter[]>(metersKey(orgId), (prev) => update(prev ?? []));
		},
		[queryClient, orgId],
	);

	// Supabase Realtime — aggiornamento live dei misuratori.
	useEffect(() => {
		if (!memberId) return;
		return subscribeToTable(
			`meters-realtime-${memberId}`,
			{ event: '*', schema: 'public', table: 'meters' },
			(payload) => {
				// Realtime payloads are raw snake_case DB rows; map them back to the
				// camelCase shape the REST API returns, else fields like telemetryMode
				// arrive undefined and clobber the row (toggle flickers to simulation).
				if (payload.eventType === 'INSERT') {
					const newMeter = fromRow('meters', payload.new) as unknown as Meter;
					if (newMeter.memberId !== memberId) return;
					patchCache((prev) => (prev.some((p) => p.id === newMeter.id) ? prev : [...prev, newMeter]));
				} else if (payload.eventType === 'UPDATE') {
					// `fromRow('meters', …)` only maps columns still on the `meters` table.
					// POD fields (codice, dso, cabinaPrimaria, lat, lon) and impianto fields
					// (potenzaKwp, codiceGaudi, tecnologia, …) moved to `pods` /
					// `production_plants` (METER_EMBED in apps/api/src/data/meters.ts) and
					// never appear in this payload — replacing the whole cached row would
					// wipe them from the screen the instant this event lands, even though
					// nothing was lost in the database. Merge onto the existing row instead.
					const patch = fromRow('meters', payload.new) as Partial<Meter>;
					patchCache((prev) => prev.map((p) => (p.id === patch.id ? { ...p, ...patch } : p)));
				} else if (payload.eventType === 'DELETE') {
					const deletedId = (payload.old as { id: string }).id;
					patchCache((prev) => prev.filter((p) => p.id !== deletedId));
				}
			},
		);
	}, [memberId, patchCache]);

	const addMeter = useCallback(
		async (data: Omit<Meter, 'id' | 'tenantId' | 'createdAt'>) => {
			const created = await api.post<Meter>(`/api/meters?orgId=${orgId}`, data);
			patchCache((prev) => (prev.some((p) => p.id === created.id) ? prev : [...prev, created]));
			return created;
		},
		[orgId, patchCache],
	);

	const updateMeter = useCallback(
		async (id: string, data: Partial<Omit<Meter, 'id' | 'tenantId'>>) => {
			const updated = await api.patch<Meter>(`/api/meters/${id}?orgId=${orgId}`, data);
			patchCache((prev) => prev.map((p) => (p.id === id ? updated : p)));
			void queryClient.invalidateQueries({ queryKey: ['meter-hourly', orgId, id] });
			return updated;
		},
		[orgId, patchCache, queryClient],
	);

	const deleteMeter = useCallback(
		async (id: string) => {
			await api.delete(`/api/meters/${id}?orgId=${orgId}`);
			patchCache((prev) => prev.filter((p) => p.id !== id));
		},
		[orgId, patchCache],
	);

	const meters = data ?? EMPTY;

	return {
		meters,
		isLoading: active ? isLoading : false,
		error: error
			? error instanceof Error
				? error.message
				: 'Errore nel caricamento misuratori'
			: null,
		refetch,
		addMeter,
		updateMeter,
		deleteMeter,
		// ── deprecated aliases (kept one release while callers migrate) ──────────
		pods: meters,
		addPod: addMeter,
		updatePod: updateMeter,
		deletePod: deleteMeter,
	};
}

// ── Deprecated aliases ──────────────────────────────────────────────────────
/** @deprecated use {@link Meter} */
export type DBPod = Meter;

/**
 * Tutti i punti di misura della configurazione in area admin, dalla stessa
 * cache di {@link useMeters}: per le viste che guardano la configurazione
 * intera, come la scadenza della domanda GSE (INB-367).
 */
export function useConfigMeters() {
	const orgId = useAdminScopeStore((s) => s.configId);
	const { data, isLoading } = useQuery({
		queryKey: metersKey(orgId),
		enabled: !!orgId,
		queryFn: () => api.get<Meter[]>('/api/meters', { orgId: orgId as string }),
	});
	return { meters: data ?? [], isLoading };
}
